import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setCalendarDate,
  setCollectionId,
  setCollections,
  setCustomers,
} from "./collectionsSlice";
import { TRootState } from "../../../../store";
import useAxiosClient from "../../../../hooks/useAxiosClient";
import {
  CollectionDataResponse,
  mapPayoutResponseToModel,
} from "../models/collectionsModels";
import { TCalendarType } from "../../../../components/commons/ono-calendar-v2/calendar_types";
import { setPayoutViewId } from "../../payout_view/bloc/payoutViewSlice";
import {
  CustomerProfile,
  mapCustomersResponseToModel,
} from "../../payouts/models/payoutModels";
import { getUpdatedCollections } from "../functions/collections_functions";
import { toast } from "react-toastify";
import { getReportFileName } from "../../../reports/functions";
import * as XLSX from "xlsx";
const useCollections = () => {
  const { calendarDate, collections, adminId, customers, filters } =
    useSelector((state: TRootState) => ({
      calendarDate: state.collections.calendarDate,
      collections: state.collections.collections,
      adminId: state.auth.adminId,
      customers: state.collections.customers,
      filters: state.collections.filters,
    }));

  const [searchText, setSearchText] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showPayoutView, setShowPayoutView] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [collectionSummary, setCollectionSummary] = useState({
    total: 0,
    noOfCollections: 0,
  });

  const dispatch = useDispatch();

  const { handleGetCall } = useAxiosClient();

  const calendarDateRef = useRef(calendarDate);

  const isDateChanged =
    calendarDateRef.current.fromDate != calendarDate.fromDate ||
    calendarDateRef.current.toDate != calendarDate.toDate ||
    calendarDateRef.current.mode != calendarDate.mode;

  useEffect(() => {
    if (collections.length <= 0 || isDateChanged) {
      _getCollections();
    }
    if (customers.length <= 0) {
      _getCustomers();
    }
    calendarDateRef.current = calendarDate;
  }, [calendarDate]);

  // For caculating the collections summary card data
  useEffect(() => {
    _updateCollectionSummary();
  }, [searchText, collections.length, filters]);

  const _updateCollectionSummary = () => {
    let updatedCollections = getUpdatedCollections(
      searchText,
      collections,
      filters,
    );

    let successCollections = updatedCollections.filter(
      (c) => c.status === "SUCCESS",
    );
    let total = successCollections.reduce((t, p) => t + p.amount, 0);
    setCollectionSummary({
      total: total,
      noOfCollections: successCollections.length,
    });
  };

  const _getCollections = async () => {
    handleGetCall<CollectionDataResponse[]>(
      `payments/payouts/collections/adminId/${adminId}?fromDate=${calendarDate.fromDate}&toDate=${calendarDate.toDate}`,
    ).then((res) => {
      if (res.data != null) {
        dispatch(
          setCollections({
            collections: mapPayoutResponseToModel(res.data),
          }),
        );
      } else
        dispatch(
          setCollections({
            collections: [],
          }),
        );
    });
  };

  const _getCustomers = () => {
    handleGetCall<CustomerProfile[]>(
      `payments/payouts/profile/adminId/${adminId}?type=C`,
    ).then((res) => {
      if (res.data != null) {
        dispatch(
          setCustomers({
            customers: mapCustomersResponseToModel(res.data),
          }),
        );
      }
    });
  };

  const onChangeDate = (fromDate: string, toDate: string, mode: string) => {
    dispatch(
      setCalendarDate({
        calendarDate: {
          fromDate: fromDate,
          toDate: toDate,
          mode: mode as TCalendarType,
        },
      }),
    );
  };

  const onRefresh = () => {
    _getCollections();
    _updateCollectionSummary();
  };

  const updatedCollections = getUpdatedCollections(
    searchText,
    collections,
    filters,
  );

  const onClickCollection = (index: number) => {
    let utr = updatedCollections[index].utr;
    setShowPayoutView(true);
    dispatch(setCollectionId({ collectionId: utr }));
    dispatch(setPayoutViewId({ utr: utr, payoutType: "COLLECTION" }));
  };

  const isFilterApplied = () => {
    return (
      filters.selectedModes.length > 0 ||
      filters.selectedStatuses.length > 0 ||
      filters.selectedCustomers.length > 0
    );
  };
  const handleCollectionsExport = () => {
    const headers = [
      "Trade Id / Loan Id",
      "UTR",
      "Created At",
      "Customer",
      "Payment source",
      "Amount",
      "Status",
    ];

    const data = collections.map((item) => ({
      prodId: item.prodId,
      utr: item.utr,
      createdOn: item.createdOn,
      customer: item.customer,
      paymentSource: item.paymentSource,
      amount: item.amount,
      status: item.status,
    }));

    const exportData = [
      headers,
      ...data.map((row) => [
        row.prodId,
        row.utr,
        row.createdOn,
        row.customer,
        row.paymentSource,
        row.amount,
        row.status,
      ]),
    ];

    if (data.length > 0) {
      const worksheet = XLSX.utils.aoa_to_sheet(exportData);

      const totalRows = exportData.length;
      const totalCols = exportData[0]?.length || 0;

      worksheet["!rows"] = Array(totalRows).fill({ hpt: 22 });

      worksheet["!cols"] = Array(totalCols).fill({ wch: 18 });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Reports");
      XLSX.writeFile(
        workbook,
        getReportFileName({
          reportName: "Collections",
          reportType: calendarDate.mode,
          fromDate: calendarDate.fromDate,
          toDate: calendarDate.toDate,
        }),
      );
    } else {
      toast.error("No data available to export excel.");
    }
  };
  return {
    searchText,
    setSearchText,
    calendarDate,
    collections,
    onChangeDate,
    onRefresh,
    onClickCollection,
    collectionSummary,
    isCollapsed,
    setIsCollapsed,
    showPayoutView,
    setShowPayoutView,
    showFilters,
    setShowFilters,
    filters,
    isFilterApplied,
    handleCollectionsExport,
  };
};

export default useCollections;
